<script setup>
import { useIncidentStore } from '@/store/incident'
import { computed, onMounted, ref, watch } from 'vue'
import DailySegmentIncidentTable from '@/components/incidents/DailySegmentIncidentTable.vue'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { humanReadableDuration } from '@/composables/datetime'
import { useRoute, useRouter } from 'vue-router'
import i18n from '@/plugins/i18n'
import { splittingModes } from '@/composables/incidents/incidentPlottingHelpers'
import { DateTime } from 'luxon'
import DailyTimestampIncidentTable from '@/components/incidents/DailyTimestampIncidentTable.vue'
import OutdatedAlert from '@/components/OutdatedAlert.vue'
import { useDisplay } from 'vuetify'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import GenericTimeSeriesChart from '@/composables/charts/GenericTimeSeriesChart.vue'
import { IncidentBarChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IncidentBarChartBuilder.ts'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES.ts'
import { GeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup.ts'
const incidentStore = useIncidentStore()
const route = useRoute()
const router = useRouter()
const { t } = i18n.global
const { width } = useDisplay()

const maxTimeShift = 23
const minTimeShift = 0

const props = defineProps({
  useQuery: { type: Boolean, default: false },
  preDefinedDays: { type: Number, default: 3 },
  preDefinedTimeResolution: { type: Number, default: 6 },
  showSettings: { type: Boolean, default: true },
})

const segmentLengths = [1, 2, 4, 6, 8, 12, 24]
const availableTimeShifts = Array.from({ length: maxTimeShift + 1 }, (_, i) => i).map(i => ({
  text: humanReadableDuration(i * 60 * 60 * 1000, undefined, ['h']),
  value: i,
}))
const availableSegmentLengths = segmentLengths.map(length => ({
  value: length,
  title: humanReadableDuration(length * 60 * 60 * 1000),
}))

const incidents = ref([])
const showTimeShiftDialog = ref(false)
const showResolutionDialog = ref(false)
const start = ref(DateTime.now())
const end = ref(DateTime.now())
const timeResolution = ref(props.preDefinedTimeResolution)
const timeShift = ref(0)
const selectedTimeShift = ref(0)
const selectedTimeResolution = ref(props.preDefinedTimeResolution)
const selectedMode = ref(splittingModes.open)
const selectedStart = ref(DateTime.now())
const selectedEnd = ref(DateTime.now())

const loading = ref(false)
const mode = ref(splittingModes.open)
const modeDialog = ref(false)

const isBelow1100px = computed(() => width.value < 1100)
const isBelow1800px = computed(() => width.value < 1800)

const isOutdated = computed(() => {
  if (selectedTimeResolution.value !== timeResolution.value) return true
  if (selectedTimeShift.value !== timeShift.value) return true
  if (selectedStart.value !== start.value) return true
  if (selectedEnd.value !== end.value) return true
  if (selectedMode.value !== mode.value) return true
  return false
})

const splittingModeColor = computed(() => {
  if (selectedMode.value === splittingModes.opened) return 'orange'
  if (selectedMode.value === splittingModes.closed) return 'green'
  return 'blue'
})

const segmentOrTimeResolutionLabel = computed(() => {
  if (selectedMode.value === splittingModes.open) return t('dashboards.dead_animal_statistics.time_resolution')
  return t('dashboards.dead_animal_statistics.segment_length')
})

const segmentOrTimeResolutionExplanation = computed(() => {
  if (selectedMode.value === splittingModes.open) return t('dashboards.dead_animal_statistics.time_resolution_explanation')
  return t('dashboards.dead_animal_statistics.segment_explanation')
})

const amountOfIncidents = computed(() => {
  if (incidents.value && incidents.value.length > 0) return `(${incidents.value.length})`
  return null
})

watch(
  () => [start.value, end.value, timeResolution.value, mode.value, timeShift.value],
  () => {
    if (props.useQuery) {
      const query = {
        start: start.value.toMillis(),
        end: end.value.toMillis(),
        timeResolution: timeResolution.value,
        mode: mode.value,
        timeShift: timeShift.value,
      }
      router.replace({ query })
    }
  }
)

async function load() {
  timeResolution.value = selectedTimeResolution.value
  timeShift.value = selectedTimeShift.value
  mode.value = selectedMode.value
  start.value = selectedStart.value
  end.value = selectedEnd.value

  loading.value = true
  incidents.value = await incidentStore.loadIncidentsOpenBetweenSpecificTimestamps(
    ['deadAnimal'],
    start.value.minus({ hours: minTimeShift }).toMillis(),
    end.value.plus({ hours: maxTimeShift }).toMillis(),
    {
      only: ['state', 'incidentId', 'firstEventTimestamp', 'lastEventTimestamp', 'type'],
    },
    { firstEventTimestamp: 'asc' },
    [
      {
        key: 'messagePayload.falsePositive',
        operator: 'exists',
        value: false,
      },
    ],
    undefined
  )
  loading.value = false
}

onMounted(async () => {
  start.value = DateTime.now().minus({ days: props.preDefinedDays }).startOf('day')
  end.value = DateTime.now()

  if (props.useQuery) {
    const queryParams = route.query
    if (queryParams.start) start.value = DateTime.fromMillis(parseInt(queryParams.start))
    if (queryParams.end) end.value = DateTime.fromMillis(parseInt(queryParams.end))
    if (queryParams.timeResolution) timeResolution.value = parseInt(queryParams.timeResolution)
    if (queryParams.mode) mode.value = queryParams.mode
    if (queryParams.timeShift) timeShift.value = parseInt(queryParams.timeShift)
  }

  selectedStart.value = start.value
  selectedEnd.value = end.value
  selectedMode.value = mode.value
  selectedTimeShift.value = timeShift.value
  selectedTimeResolution.value = timeResolution.value
  await load()
})
</script>

<template>
  <div class="d-flex flex-column ga-8">
    <div v-if="showSettings">
      <v-card-title>
        {{ $t('dashboards.dead_animal_statistics.loading_settings') }}
      </v-card-title>
      <v-layout-card class="px-4">
        <div
          :class="isBelow1800px ? 'd-flex flex-column align-center ga-8' : 'd-flex flex-row justify-center align-center ga-6'"
          style="width: 100%; max-width: 2000px; margin: 0 auto"
        >
          <div :class="isBelow1100px ? 'flex-fill w-100 d-flex flex-column' : 'flex-fill w-100 d-flex flex-row align-start ga-6'">
            <!-- time settings row -->
            <div
              :class="isBelow1100px ? 'flex-fill d-flex flex-column justify-start align-center mb-8' : 'd-flex flex-row justify-center ga-8'"
              style="flex: 2"
            >
              <DateTimePicker
                v-model="selectedStart"
                :label="$t('general_interface.date_time_picker.start')"
                fullwidth
                style="max-width: 250px"
                @enter="load"
              />
              <DateTimePicker
                v-model="selectedEnd"
                :label="$t('general_interface.date_time_picker.end')"
                fullwidth
                style="max-width: 250px"
                @enter="load"
              />
            </div>

            <v-divider
              v-if="!isBelow1100px"
              vertical
            />

            <!-- change mode button -->
            <div
              class="d-flex flex-column"
              style="flex: 1"
            >
              <v-label class="ms-2">
                {{ t('dashboards.dead_animal_statistics.display_mode') }}
              </v-label>
              <v-btn
                variant="outlined"
                width="100%"
                :color="splittingModeColor"
                size="large"
                min-height="54"
                append-icon="mdi-chevron-down"
                :prepend-icon="
                  selectedMode === splittingModes.open
                    ? 'mdi-lock-open-variant'
                    : selectedMode === splittingModes.opened
                      ? 'mdi-lock-open'
                      : 'mdi-lock'
                "
              >
                {{ t(`dashboards.dead_animal_statistics.modes.${selectedMode}.title`) }}

                <GenericCustomDialog
                  v-model="modeDialog"
                  activator="parent"
                  max-width="800px"
                  width="100%"
                  body-class="d-flex flex-column ga-4"
                >
                  <template #headerIcon>
                    <v-icon icon="mdi-filter-variant" />
                  </template>
                  <template #title>
                    {{ t('dashboards.dead_animal_statistics.display_mode') }}
                  </template>
                  <template #body>
                    <!-- open card -->
                    <v-layout-card
                      variant="flat"
                      elevation="0"
                      color="blue"
                      @click="
                        () => {
                          selectedMode = splittingModes.open
                          modeDialog = false
                        }
                      "
                    >
                      <div class="px-4 d-flex align-center ga-2 text-truncate">
                        <v-icon icon="mdi-lock-open-variant" />
                        <v-card-title class="px-0 flex-shrink-1 text-truncate">
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.open}.title`) }}
                        </v-card-title>
                      </div>
                      <v-card-text class="py-0">
                        {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.open}.text`) }}
                      </v-card-text>
                      <v-card-actions class="justify-end">
                        <v-btn
                          append-icon="mdi-arrow-right"
                          @click="
                            () => {
                              selectedMode = splittingModes.opened
                              modeDialog = false
                            }
                          "
                        >
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.open}.button`) }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout-card>

                    <!-- opened card -->
                    <v-layout-card
                      variant="flat"
                      elevation="0"
                      color="orange"
                      @click="
                        () => {
                          selectedMode = splittingModes.opened
                          modeDialog = false
                        }
                      "
                    >
                      <div class="px-4 d-flex align-center ga-2 text-truncate">
                        <v-icon icon="mdi-lock-open" />
                        <v-card-title class="px-0 flex-shrink-1 text-truncate">
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.opened}.title`) }}
                        </v-card-title>
                      </div>
                      <v-card-text class="py-0">
                        {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.opened}.text`) }}
                      </v-card-text>
                      <v-card-actions class="justify-end">
                        <v-btn
                          append-icon="mdi-arrow-right"
                          @click="
                            () => {
                              selectedMode = splittingModes.opened
                              modeDialog = false
                            }
                          "
                        >
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.opened}.button`) }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout-card>

                    <!-- closed card -->
                    <v-layout-card
                      variant="flat"
                      elevation="0"
                      color="green"
                      @click="
                        () => {
                          selectedMode = splittingModes.closed
                          modeDialog = false
                        }
                      "
                    >
                      <div class="px-4 d-flex align-center ga-2 text-truncate">
                        <v-icon icon="mdi-lock" />
                        <v-card-title class="px-0 flex-shrink-1 text-truncate">
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.closed}.title`) }}
                        </v-card-title>
                      </div>
                      <v-card-text class="py-0">
                        {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.closed}.text`) }}
                      </v-card-text>
                      <v-card-actions class="justify-end">
                        <v-btn
                          append-icon="mdi-arrow-right"
                          @click="
                            () => {
                              selectedMode = splittingModes.closed
                              modeDialog = false
                            }
                          "
                        >
                          {{ $t(`dashboards.dead_animal_statistics.modes.${splittingModes.closed}.button`) }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout-card>
                  </template>
                </GenericCustomDialog>
              </v-btn>
            </div>
          </div>

          <v-divider
            v-if="!isBelow1800px"
            vertical
          />

          <div
            :class="
              isBelow1100px ? 'flex-fill w-100 d-flex flex-column justify-start align-center ' : 'flex-fill w-100 d-flex flex-row align-center ga-6'
            "
          >
            <!-- interval settings row -->
            <div
              :class="isBelow1100px ? 'flex-fill d-flex flex-column mb-4' : 'd-flex flex-row justify-center ga-8'"
              style="flex: 2"
            >
              <div
                class="py-4 w-100 flex-fill"
                style="max-width: 250px"
              >
                <v-autocomplete
                  v-model="selectedTimeResolution"
                  variant="outlined"
                  :style="isBelow1100px ? 'min-width: 250px;' : 'min-width: 200px;'"
                  :hide-details="true"
                  :items="availableSegmentLengths"
                  :label="segmentOrTimeResolutionLabel"
                  item-text="title"
                  item-value="value"
                >
                  <template #append>
                    <v-btn
                      class="rounded-pill"
                      size="small"
                      variant="outlined"
                      :icon="true"
                    >
                      <v-icon icon="mdi-help" />

                      <GenericCustomDialog
                        v-model="showResolutionDialog"
                        activator="parent"
                        max-width="600px"
                        width="100%"
                        hide-cancel-button
                      >
                        <template #headerIcon>
                          <v-icon icon="mdi-clock-fast" />
                        </template>
                        <template #title>
                          {{ segmentOrTimeResolutionLabel }}
                        </template>
                        <template #body>
                          {{ segmentOrTimeResolutionExplanation }}
                        </template>
                        <template #actions>
                          <v-btn
                            variant="flat"
                            color="success"
                            prepend-icon="mdi-close"
                            @click="showResolutionDialog = false"
                          >
                            {{ $t('general_interface.buttons.close') }}
                          </v-btn>
                        </template>
                      </GenericCustomDialog>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </div>
              <div
                class="py-4 w-100 flex-fill"
                style="max-width: 250px"
              >
                <v-autocomplete
                  v-model="selectedTimeShift"
                  :return-object="false"
                  variant="outlined"
                  :style="isBelow1100px ? 'min-width: 250px;' : 'min-width: 200px;'"
                  :hide-details="true"
                  :items="availableTimeShifts"
                  item-value="value"
                  item-title="text"
                  :label="$t('dashboards.dead_animal_statistics.time_shift')"
                >
                  <template #append>
                    <v-btn
                      class="rounded-pill"
                      size="small"
                      variant="outlined"
                      :icon="true"
                    >
                      <v-icon icon="mdi-help" />

                      <GenericCustomDialog
                        v-model="showTimeShiftDialog"
                        activator="parent"
                        max-width="600px"
                        width="100%"
                        hide-cancel-button
                      >
                        <template #headerIcon>
                          <v-icon icon="mdi-clock-start" />
                        </template>
                        <template #title>
                          {{ $t('dashboards.dead_animal_statistics.time_shift') }}
                        </template>
                        <template #body>
                          {{ $t('dashboards.dead_animal_statistics.time_shift_explanation') }}
                        </template>
                        <template #actions>
                          <v-btn
                            variant="flat"
                            color="success"
                            prepend-icon="mdi-close"
                            @click="showTimeShiftDialog = false"
                          >
                            {{ $t('general_interface.buttons.close') }}
                          </v-btn>
                        </template>
                      </GenericCustomDialog>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </div>
            </div>

            <v-divider
              v-if="!isBelow1100px"
              vertical
            />

            <!-- load button -->
            <div
              :class="isBelow1100px ? 'w-100' : 'd-flex flex-column'"
              style="flex: 1"
            >
              <v-btn
                variant="flat"
                color="primary"
                size="large"
                width="100%"
                @click="load"
              >
                {{ $t('general_interface.buttons.reload') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-layout-card>
    </div>

    <!-- dead_animal_statistics -->
    <div>
      <v-card-title>
        {{
          $t('dashboards.dead_animal_statistics.title', {
            amount: amountOfIncidents,
          })
        }}
      </v-card-title>
      <v-layout-card v-if="loading">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate />
        </v-card-text>
      </v-layout-card>

      <GenericTimeSeriesChart
        :chart-builder="
          new IncidentBarChartBuilder({
            chartType: CHART_TYPES.INCIDENT_BAR,
            timeSetup: GeneralChartTimeSetup.lastWeek('barnManagement', 1000 * 60 * 60 * 6).serialize(),
            title: $t('charts.titles.deadAnimals'),
            subtitle: $t('charts.subtitles.deadAnimals'),
            incidentTypes: ['deadAnimal'],
            timestampMode: 'opened',
            additionalFilters: [
              { key: 'hidden', operator: '==', value: false },
              { key: 'messagePayload.lastDetection.regions.stallbereich_lr', operator: 'exists', value: true },
            ],
          })
        "
      ></GenericTimeSeriesChart>
    </div>

    <!-- incident table -->
    <div v-if="incidents && incidents.length > 0 && !loading">
      <v-card-title>
        {{
          $t('dashboards.dead_animal_statistics.incident_table_title', {
            amount: amountOfIncidents,
          })
        }}
      </v-card-title>
      <DailyTimestampIncidentTable
        v-if="mode === splittingModes.open"
        class="rounded-lg elevation-2"
        :start="start.toMillis()"
        :end="end.toMillis()"
        :time-resolution="timeResolution"
        :time-shift="timeShift"
        :incidents="incidents"
        :dimension-key="null"
      />
      <DailySegmentIncidentTable
        v-if="mode !== splittingModes.open"
        class="rounded-lg elevation-2"
        :mode="mode"
        :start="start.toMillis()"
        :end="end.toMillis()"
        :time-resolution="timeResolution"
        :time-shift="timeShift"
        :incidents="incidents"
        :dimension-key="null"
      />
    </div>
  </div>

  <OutdatedAlert
    v-if="isOutdated"
    @click="load"
  />
</template>

<style scoped></style>
